import axios, { AxiosResponse } from 'axios';
import { AuthResponse, LoginFormData, RegisterFormData, User } from '../types';

const API_BASE_URL = 'https://fg8tpvs5tl.execute-api.ap-northeast-1.amazonaws.com/dev';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 認証なしのAPIクライアント（パブリック用）
const publicApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// リクエストインターセプター: JWTトークンを自動で追加
apiClient.interceptors.request.use((config) => {
  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// レスポンスインターセプター: 401エラーでログアウト
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // 401エラーでも自動リダイレクトしない（口コミ一覧ページで必要）
      console.warn('401 Unauthorized - but not redirecting');
      localStorage.removeItem('token');
      // window.location.href = '/login'; // コメントアウト
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (data: LoginFormData): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await apiClient.post('/auth/login', data);
    return response.data;
  },

  register: async (data: RegisterFormData): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await apiClient.post('/auth/register', data);
    return response.data;
  },

  confirmEmail: async (email: string, code: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.post('/auth/confirm', {
      email,
      code
    });
    return response.data;
  },

  resendConfirmationCode: async (email: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.post('/auth/resend', {
      email
    });
    return response.data;
  },

  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.post('/auth/forgot-password', {
      email
    });
    return response.data;
  },

  resetPassword: async (email: string, code: string, newPassword: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.post('/auth/reset-password', {
      email,
      confirmationCode: code,
      newPassword
    });
    return response.data;
  },

  getProfile: async (): Promise<User> => {
    const response: AxiosResponse<User> = await apiClient.get('/auth/profile');
    return response.data;
  },

  getUserRole: async (): Promise<{ role: string }> => {
    const response: AxiosResponse<{ role: string }> = await apiClient.get('/auth/user-role');
    return response.data;
  },

  updateProfile: async (data: import('../types').UpdateProfileData): Promise<User> => {
    if (data.profile_image) {
      // 画像がある場合はFormDataを使用
      const formData = new FormData();
      
      if (data.nickname) {
        formData.append('nickname', data.nickname);
      }
      
      formData.append('profile_image', data.profile_image);
      
      const response: AxiosResponse<User> = await apiClient.put('/auth/profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      // 画像がない場合はJSONを使用
      const response: AxiosResponse<User> = await apiClient.put('/auth/profile', {
        nickname: data.nickname,
      });
      return response.data;
    }
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.put('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  // 居住歴API
  addResidenceHistory: async (data: import('../types').AddResidenceData): Promise<import('../types').ResidenceHistory> => {
    const response: AxiosResponse<import('../types').ResidenceHistory> = await apiClient.post('/auth/residence-history', data);
    return response.data;
  },

  updateResidenceHistory: async (id: string, data: import('../types').AddResidenceData): Promise<import('../types').ResidenceHistory> => {
    const response: AxiosResponse<import('../types').ResidenceHistory> = await apiClient.put(`/auth/residence-history/${id}`, data);
    return response.data;
  },

  deleteResidenceHistory: async (id: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await apiClient.delete(`/auth/residence-history/${id}`);
    return response.data;
  },

  // 住所検索API
  searchAddress: async (zipcode: string): Promise<{ addresses: import('../types').AddressSearchResult[] }> => {
    const response: AxiosResponse<{ addresses: import('../types').AddressSearchResult[] }> = await apiClient.get('/auth/address/search', {
      params: { zipcode }
    });
    return response.data;
  },

  // ユーザーデータ移行API
  migrateUserData: async (): Promise<{ message: string; old_user_id?: string; new_user_id?: string; migrated_data?: any }> => {
    const response: AxiosResponse<{ message: string; old_user_id?: string; new_user_id?: string; migrated_data?: any }> = await apiClient.post('/auth/migrate-user-data');
    return response.data;
  },
};

export const reviewApi = {
  createReview: async (reviewData: import('../types').ReviewFormData): Promise<{ message: string; review_id: string }> => {
    const response: AxiosResponse<{ message: string; review_id: string }> = await apiClient.post('/reviews', reviewData);
    return response.data;
  },

  getReviews: async (params?: { prefecture_code?: string; city_code?: string; location_key?: string }): Promise<{ reviews: import('../types').Review[]; count: number }> => {
    const response: AxiosResponse<{ reviews: import('../types').Review[]; count: number }> = await apiClient.get('/reviews', { params });
    return response.data;
  },

  getPublicReviews: async (params?: { prefecture_code?: string; city_code?: string; location_key?: string }): Promise<{ reviews: import('../types').Review[]; count: number; is_public_view: boolean; message?: string }> => {
    const response: AxiosResponse<{ reviews: import('../types').Review[]; count: number; is_public_view: boolean; message?: string }> = await publicApiClient.get('/reviews/public', { params });
    return response.data;
  },

  getUserReviewStats: async (): Promise<import('../types').UserReviewStats> => {
    const response: AxiosResponse<import('../types').UserReviewStats> = await apiClient.get('/reviews/user/stats');
    return response.data;
  },

  reportReview: async (reviewId: string, reason: string): Promise<{ message: string; report_id: string }> => {
    const response: AxiosResponse<{ message: string; report_id: string }> = await apiClient.post(`/reviews/${reviewId}/report`, {
      reason: reason
    });
    return response.data;
  },
};

export const newsApi = {
  getNews: async (params?: { prefecture?: string; keyword?: string }): Promise<{ articles: any[]; count: number }> => {
    const response: AxiosResponse<{ articles: any[]; count: number }> = await publicApiClient.get('/news', { params });
    return response.data;
  },

  syncNotionArticles: async (): Promise<{ success: boolean; synced_count: number; total_pages: number }> => {
    const response: AxiosResponse<{ success: boolean; synced_count: number; total_pages: number }> = await publicApiClient.get('/notion/sync-blog-posts');
    return response.data;
  },
};

export const blogApi = {
  getPosts: async (params?: any): Promise<any> => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    const response: AxiosResponse<any> = await publicApiClient.get(`/blog/posts${query}`);
    return response.data;
  },

  getPost: async (postId: string): Promise<any> => {
    const response: AxiosResponse<any> = await publicApiClient.get(`/blog/posts/${postId}`);
    return response.data;
  },

  createReaction: async (postId: string, reactionType: string): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.post(`/blog/posts/${postId}/reactions`, {
      reaction_type: reactionType
    });
    return response.data;
  }
};

// 管理画面用API
export const adminApi = {
  // ダッシュボード
  getDashboard: async (): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.get('/admin/dashboard');
    return response.data;
  },

  // ユーザー管理
  getUsers: async (params?: any): Promise<any> => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    const response: AxiosResponse<any> = await apiClient.get(`/admin/users${query}`);
    return response.data;
  },

  suspendUser: async (userId: string, reason?: string): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.put(`/admin/users/${userId}/suspend`, {
      reason
    });
    return response.data;
  },

  activateUser: async (userId: string): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.put(`/admin/users/${userId}/activate`);
    return response.data;
  },

  deleteUser: async (userId: string, reason?: string): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.put(`/admin/users/${userId}/delete`, {
      reason
    });
    return response.data;
  },

  updateUserRole: async (userId: string, role: string): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.put(`/admin/users/${userId}/role`, {
      role
    });
    return response.data;
  },

  // 口コミ管理
  getReviews: async (params?: any): Promise<any> => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    const response: AxiosResponse<any> = await apiClient.get(`/admin/reviews${query}`);
    return response.data;
  },

  deleteReview: async (reviewId: string, reason: string): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.delete(`/admin/reviews/${reviewId}`, {
      data: { reason }
    });
    return response.data;
  },

  // 通報管理
  getReports: async (params?: any): Promise<any> => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    const response: AxiosResponse<any> = await apiClient.get(`/admin/reports${query}`);
    return response.data;
  },

  processReport: async (reportId: string, action: 'approve' | 'reject', adminNote?: string): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.put(`/admin/reports/${reportId}`, {
      action,
      admin_note: adminNote
    });
    return response.data;
  },

  // ブログ管理
  getBlogPosts: async (params?: any): Promise<any> => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    const response: AxiosResponse<any> = await apiClient.get(`/admin/blog/posts${query}`);
    return response.data;
  },

  getBlogPost: async (postId: string): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.get(`/admin/blog/posts/${postId}`);
    return response.data;
  },

  createBlogPost: async (postData: any): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.post('/admin/blog/posts', postData);
    return response.data;
  },

  updateBlogPost: async (postId: string, postData: any): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.put(`/admin/blog/posts/${postId}`, postData);
    return response.data;
  },

  deleteBlogPost: async (postId: string): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.delete(`/admin/blog/posts/${postId}`);
    return response.data;
  },

  // Notion API統合
  autoGenerateArticle: async (data: {
    topic: string;
    prefecture?: string;
    city?: string;
  }): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.post('/notion/auto-generate', data);
    return response.data;
  },

  syncNotionPages: async (): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.post('/notion/sync');
    return response.data;
  },

  publishNotionPage: async (pageId: string): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.post('/notion/publish', { page_id: pageId });
    return response.data;
  },

  // Notionデータベース同期関連
  syncBlogPostsFromNotion: async (): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.get('/notion/sync-blog-posts');
    return response.data;
  },

  getBlogPostsFromNotion: async (params?: {
    limit?: number;
    category?: string;
    prefecture?: string;
  }): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.get('/notion/blog-posts', { params });
    return response.data;
  },

  uploadBlogImage: async (imageFile: File): Promise<any> => {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    const response: AxiosResponse<any> = await apiClient.post('/admin/blog/images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
};

// 通報API
export const reportApi = {
  reportReview: async (reviewId: string, reason: string, description?: string): Promise<any> => {
    const response: AxiosResponse<any> = await apiClient.post(`/reviews/${reviewId}/report`, {
      reason,
      description
    });
    return response.data;
  }
};

export default apiClient;
